import { cloneElement, isValidElement, type CSSProperties, type ReactNode } from 'react';
import type { PortableTextComponents } from '@portabletext/react';

import { richTextComponent } from '@/sanity/sanityPortableText';

import hatch from '@/public/patrick-affiliate/hero-hatch.png';
import hatchLight from '@/public/patrick-affiliate/hatch-light.png';

const LINE_CENTRE_LIFT = '0.0875em';

const WRAPPER_CLASS = [
  'relative inline whitespace-nowrap !font-blacker italic font-normal leading-[0]',
  '[--hl-wash-d:5.875rem] lg:[--hl-wash-d:9.25rem]',
  '[--hl-ext-d:0.375rem] lg:[--hl-ext-d:0rem]',
  '[--hl-tracking-d:-0.05em]',
  '[--hl-pl-d:0.375rem] [--hl-pr-d:0.15em]',
  '[--hl-sr:0.45rem] lg:[--hl-sr:0.75rem]',
  '[--hl-sl:0.3833rem] lg:[--hl-sl:0.6395rem]',
  '[--hl-tex:0.75rem] lg:[--hl-tex:1.25rem]',
  '[&_em]:pr-0',
  // typography.scss gives every span the Geist family, which would beat inheritance here.
  '[&_span]:!font-blacker',
].join(' ');

const WRAPPER_STYLE = {
  '--hl-w': 'var(--hl-wash, var(--hl-wash-d))',
  '--hl-x': 'var(--hl-ext, var(--hl-ext-d))',
  '--hl-p-l': 'var(--hl-pl, var(--hl-pl-d))',
  '--hl-p-r': 'var(--hl-pr, var(--hl-pr-d))',
  '--hl-r': 'max(1px, 0.1875rem)',
  '--hl-top': `calc(50% - ${LINE_CENTRE_LIFT} + var(--hl-dy, 0.125rem) - var(--hl-w) / 2)`,
  '--hl-reach-l': 'calc(var(--hl-p-l) + 0.25rem + var(--hl-sl))',
  '--hl-reach-r': 'calc(var(--hl-p-r) + 0.25rem + var(--hl-sr))',
  // Inline here, a bare --hl-ctr would outrank the section's class, hence the -d default.
  '--hl-ctr-d': 'calc((var(--hl-reach-r) - var(--hl-reach-l)) / 2)',
  '--hl-c': 'var(--hl-ctr, var(--hl-ctr-d))',
  letterSpacing: 'var(--hl-tracking, var(--hl-tracking-d))',
  marginLeft: 'calc(var(--hl-ml, 0px) - var(--hl-c))',
  marginRight: 'calc(var(--hl-mr, 0px) + var(--hl-c))',
} as CSSProperties;

const DECO_STYLE = {
  display: 'var(--hl-deco, block)',
} as CSSProperties;

const WASH_BOX: CSSProperties = {
  ...DECO_STYLE,
  top: 'var(--hl-top)',
  left: 'calc(-1 * var(--hl-p-l))',
  right: 'calc(-1 * var(--hl-p-r))',
  height: 'var(--hl-w)',
};

type Tone = 'light' | 'dark' | 'forest';

const TONES: Record<
  Tone,
  { wash: string; tick: string; text: string; texture: string }
> = {
  light: {
    wash: 'rgba(34,142,113,0.12)',
    tick: '#228E71',
    text: '#003828',
    texture: hatch.src,
  },
  dark: {
    wash: 'rgba(218,255,245,0.12)',
    tick: '#BAE9D6',
    text: '#D6EEE7',
    texture: hatchLight.src,
  },
  forest: {
    wash: 'rgba(11,91,69,0.1)',
    tick: '#0B5B45',
    text: '#003828',
    texture: hatch.src,
  },
};

// Not `\s`: it would also eat the zero-width characters stega appends in draft mode.
const LEAD = /^[ \t\r\n]+/;
const TRAIL = /[ \t\r\n]+$/;

function trimEdges(children: ReactNode): [string, ReactNode, string] {
  if (typeof children === 'string') {
    const before = children.match(LEAD)?.[0] ?? '';
    const after = children.match(TRAIL)?.[0] ?? '';
    const core = children.slice(before.length, children.length - after.length);
    return core ? [before, core, after] : ['', children, ''];
  }
  if (Array.isArray(children)) {
    if (children.length === 1) return trimEdges(children[0]);
    const items = [...children];
    let before = '';
    let after = '';
    if (typeof items[0] === 'string') {
      const [b, core] = trimEdges(items[0]);
      before = b;
      items[0] = core;
    }
    const last = items.length - 1;
    if (typeof items[last] === 'string') {
      const [, core, a] = trimEdges(items[last]);
      after = a;
      items[last] = core;
    }
    return [before, items, after];
  }
  if (isValidElement<{ children?: ReactNode }>(children)) {
    const [before, core, after] = trimEdges(children.props.children);
    if (!before && !after) return ['', children, ''];
    return [before, cloneElement(children, undefined, core), after];
  }
  return ['', children, ''];
}

export function MeasuredHighlight({
  children,
  tone = 'light',
}: {
  children: ReactNode;
  tone?: Tone;
}) {
  const { wash, tick, text, texture } = TONES[tone];
  const [before, core, after] = trimEdges(children);

  return (
    <>
      {before}
      <span className={WRAPPER_CLASS} style={{ ...WRAPPER_STYLE, color: text }}>
        <span
          aria-hidden
          className="absolute pointer-events-none hl-wash"
          style={{ ...WASH_BOX, backgroundColor: wash }}
        />
        <span
          aria-hidden
          className="absolute pointer-events-none opacity-[0.09] hl-texture"
          style={{
            ...WASH_BOX,
            backgroundImage: `url(${texture})`,
            backgroundSize: 'var(--hl-tex) var(--hl-tex)',
            backgroundPosition: 'top left',
          }}
        />
        <span
          aria-hidden
          className="absolute pointer-events-none hl-tick-l"
          style={{
            ...DECO_STYLE,
            top: 'var(--hl-top)',
            right: 'calc(100% + var(--hl-p-l) + 0.25rem)',
            width: 'var(--hl-sl)',
          }}
        >
          <span
            className="block mx-auto"
            style={{
              width: 'var(--hl-r)',
              height: 'calc(var(--hl-w) + var(--hl-x))',
              backgroundColor: tick,
            }}
          />
          <span className="block w-full aspect-square" style={{ backgroundColor: tick }} />
        </span>
        <span
          aria-hidden
          className="absolute pointer-events-none hl-tick-r"
          style={{
            ...DECO_STYLE,
            top: 'calc(var(--hl-top) - var(--hl-sr) - 0.125rem)',
            left: 'calc(100% + var(--hl-p-r) + 0.25rem)',
            width: 'var(--hl-sr)',
          }}
        >
          <span className="block w-full aspect-square" style={{ backgroundColor: tick }} />
          <span
            className="block mx-auto"
            style={{
              width: 'var(--hl-r)',
              height: 'calc(var(--hl-w) + 0.125rem)',
              backgroundColor: tick,
            }}
          />
        </span>
        <span className="relative !font-blacker">{core}</span>
      </span>
      {after}
    </>
  );
}

const serifMarks: PortableTextComponents['marks'] = {
  serif: ({ children }) => <span className="!font-blacker">{children}</span>,
  darkGreenSerif: ({ children }) => (
    <span className="!font-blacker font-normal italic text-[#003828]">{children}</span>
  ),
  lightGreenSerif: ({ children }) => (
    <span className="!font-blacker font-normal italic text-[#D6EEE7]">{children}</span>
  ),
};

export const measuredRichText: PortableTextComponents = {
  ...richTextComponent,
  marks: {
    ...richTextComponent.marks,
    ...serifMarks,
    lightGreenHighlight: ({ children }) => (
      <MeasuredHighlight tone="light">{children}</MeasuredHighlight>
    ),
  },
};

export const measuredRichTextDark: PortableTextComponents = {
  ...richTextComponent,
  marks: {
    ...richTextComponent.marks,
    ...serifMarks,
    darkGreenHighlight: ({ children }) => (
      <MeasuredHighlight tone="dark">{children}</MeasuredHighlight>
    ),
  },
};

export const measuredRichTextForest: PortableTextComponents = {
  ...richTextComponent,
  marks: {
    ...richTextComponent.marks,
    ...serifMarks,
    lightGreenHighlight: ({ children }) => (
      <MeasuredHighlight tone="forest">{children}</MeasuredHighlight>
    ),
  },
};

export const V3_HIGHLIGHT = [
  '[--hl-ext:0rem]',
  'lg:[&_span:has(>.hl-wash)]:[--hl-sr:0.5rem] lg:[&_span:has(>.hl-wash)]:[--hl-sl:0.5rem] lg:[&_span:has(>.hl-wash)]:![--hl-r:1px] lg:[--hl-ext:-0.5rem] lg:[&_span:has(>.hl-wash)]:[--hl-tex:0.9375rem]',
  'lg:[&_.hl-tick-r]:![left:calc(100%+var(--hl-p-r)-0.3125rem)] [&_.hl-tick-r]:![top:calc(var(--hl-top)-var(--hl-sr))] lg:[&_.hl-tick-r>span:last-child]:![height:calc(var(--hl-w)-0.5rem)]',
  'max-lg:[&_.hl-tick-l]:![right:calc(100%+var(--hl-p-l)+0.125rem)] lg:[&_.hl-tick-l]:![right:calc(100%+var(--hl-p-l)-0.3125rem)] lg:[&_.hl-tick-l]:![top:calc(var(--hl-top)+var(--hl-tl,0.5rem))]',
].join(' ');
